/**
 * Purpose: Render assistant markdown to safe HTML for the copilot chat
 *          bubbles. The LLM emits markdown (bold, italics, lists, code,
 *          links, tables); the widget previously printed message text as
 *          a raw React text node, so the syntax characters (`**`, `*`,
 *          backticks, `-`) showed literally. This parses markdown with
 *          `marked` and sanitises the result with DOMPurify so it can be
 *          injected via `dangerouslySetInnerHTML`.
 *
 * Implementation notes:
 *   - Input is UNTRUSTED (model + tool output), so sanitisation is
 *     mandatory: `marked` passes raw HTML in the source through verbatim,
 *     and DOMPurify is what strips `<script>`, event-handler attributes,
 *     and `javascript:` URLs. Never inject `marked` output without it.
 *   - `breaks: true` maps single newlines to <br>. Chat models lean on
 *     soft line breaks the way a person writing a message would, so this
 *     matches intent better than CommonMark's "two spaces or blank line".
 *   - Links are forced to `target="_blank"` + `rel="noopener noreferrer"`
 *     via a one-time DOMPurify hook — the widget is embedded in host
 *     pages, so an assistant link must not navigate the host away.
 *   - SSR / non-DOM guard: DOMPurify needs a `window`; when absent (a
 *     server-side import) we fall back to escaped plain text so we never
 *     throw and never emit unsanitised HTML.
 *   - Only assistant turns and the streaming buffer are rendered through
 *     this; user turns stay raw text (see `Copilot.tsx`).
 *
 * Created: 2026-06-03 08:00
 *
 * Changelog:
 *   - 2026-06-03 08:00 — Initial version. marked + DOMPurify renderer so
 *     assistant markdown renders instead of showing literal syntax.
 *   - 2026-09-25 08:02 — Add `markdownToPlainText()` for speech synthesis,
 *     so replies read aloud don't pronounce `**`, `#` or list markers.
 */
import DOMPurify from 'dompurify';
import { marked } from 'marked';
// GFM tables/strikethrough/task-lists, and soft line breaks → <br>.
marked.setOptions({ gfm: true, breaks: true });
let hookInstalled = false;
/** Install the link-hardening hook once (idempotent across calls). */
function ensureLinkHook() {
    if (hookInstalled)
        return;
    hookInstalled = true;
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
        if (node.tagName === 'A') {
            node.setAttribute('target', '_blank');
            node.setAttribute('rel', 'noopener noreferrer');
        }
    });
}
function escapeHtml(text) {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
/**
 * Parse `text` as markdown and return sanitised HTML safe for
 * `dangerouslySetInnerHTML`. On a non-DOM runtime (no `window`) returns
 * HTML-escaped plain text instead.
 */
export function renderMarkdown(text) {
    if (typeof window === 'undefined' || typeof DOMPurify.sanitize !== 'function') {
        return escapeHtml(text);
    }
    ensureLinkHook();
    const html = marked.parse(text, { async: false });
    return DOMPurify.sanitize(html);
}
/**
 * Flatten assistant markdown to plain text for speech synthesis: render
 * it, then take the text content of the sanitised HTML. Block elements
 * are separated by newlines so the TTS chunker sees sentence breaks.
 * Without a DOM (SSR) it strips the most common markdown markers.
 */
export function markdownToPlainText(text) {
    if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
        return text.replace(/[*_`#>]+/g, '').replace(/^\s*[-+]\s+/gm, '');
    }
    const html = renderMarkdown(text).replace(/<\/(p|li|h[1-6]|tr|pre|blockquote)>|<br\s*\/?>/gi, '$&\n');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent ?? '').replace(/\n{2,}/g, '\n').trim();
}
//# sourceMappingURL=markdown.js.map